import { Button, Card, CardHeader, Table, type Column } from "@helmr/ui";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { downloadCsv, fetchReport, type ReportQuery } from "../../api/reports";
import { NotFound } from "../../app/NotFound";
import { NoAccess } from "../../app/NoAccess";
import { useServices } from "../../app/services";
import { addDays } from "../../lib/dates";
import { Empty, Field, Gate, PageHead, css as s, errorText, isDate, isMonth, monthOf, useScope } from "../payroll/kit";
import { defOf, fmtDate, reportExportLever, reportLever, type Row } from "./defs";

const PAGE = 200;
const MAX_DAYS = 366;

/** `/reports/:name`. Business-local dates, session branch filter, 200 rows a page, never an unbounded range. Totals are the API's. */
export function ReportPage() {
  const { name } = useParams();
  const def = defOf(name);
  if (!def) return <NotFound />;
  return <ReportBody key={def.name} name={def.name} />;
}

function ReportBody({ name }: { name: NonNullable<ReturnType<typeof defOf>>["name"] }) {
  const def = defOf(name)!;
  const sc = useScope();
  const { client, session } = useServices();
  const [from, setFrom] = useState(addDays(sc.today, -29));
  const [to, setTo] = useState(sc.today);
  const [asOf, setAsOf] = useState(sc.today);
  const [month, setMonth] = useState(monthOf(sc.today));
  const [group, setGroup] = useState(def.groups?.[0]?.value ?? "");
  const [exportErr, setExportErr] = useState<string | null>(null);

  const rangeErr = def.controls !== "range" ? null : !isDate(from) || !isDate(to) ? "Enter both dates as YYYY-MM-DD." : from > to ? "The start date is after the end date." : to > addDays(from, MAX_DAYS - 1) ? `Pick at most ${MAX_DAYS} days.` : null;
  const ctlErr = def.controls === "asOf" ? (isDate(asOf) ? null : "Enter a date as YYYY-MM-DD.") : def.controls === "month" ? (isMonth(month) ? null : "Use YYYY-MM.") : rangeErr;
  const query: ReportQuery = {
    ...(def.controls === "range" ? { from, to } : {}),
    ...(def.controls === "month" ? { month } : {}),
    ...(def.groups ? { groupBy: group } : {}),
    branchId: sc.branchId,
  };
  const exportQuery = { ...query, ...(def.controls === "asOf" ? { asOf } : {}) } as Record<string, string | undefined>;
  // One lever per report and per report export (D57), the same ids the server gates on.
  const allowed = sc.ctx.has(reportLever(def.name));
  const q = useInfiniteQuery({
    queryKey: ["reports", name, query, asOf, ...sc.scopeKey],
    queryFn: ({ pageParam }) => fetchReport(client, name, { ...query, ...(def.controls === "asOf" ? { asOf } as Record<string, string> : {}), limit: PAGE, ...(pageParam ? { cursor: pageParam } : {}) }),
    initialPageParam: "" as string,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: allowed && !ctlErr, retry: false,
  });
  const rows = q.data?.pages.flatMap((p) => p.rows) ?? [];
  const first = q.data?.pages[0];
  const branchNames = new Map((sc.ctx.me?.branches ?? []).map((b) => [b.id as string, b.name]));
  const fmt = { money: (n: number) => sc.moneyIn(n, first?.currency ?? sc.currency, first?.locale ?? sc.locale), date: fmtDate(sc.locale), branch: (id: string) => branchNames.get(id) };
  const canExport = sc.ctx.has(reportExportLever(def.name));
  const exportCsv = async () => { setExportErr(null); try { await downloadCsv(session, `/reports/${name}/export`, exportQuery, `${name}.csv`); } catch (e) { setExportErr(errorText(e)); } };
  const cols: Array<Column<Row>> = def.columns.map((c) => ({ key: c.key, header: c.header, align: c.align, cell: (r: Row) => c.cell(r, fmt) }));

  return (
    <div className={s.page}>
      <PageHead title={def.title} sub={def.blurb}>
        <Button as={Link} to="/reports" size="sm">All reports</Button>
        {canExport && allowed ? <Button size="sm" disabled={!!ctlErr} onClick={() => void exportCsv()}>Export CSV</Button> : null}
      </PageHead>
      {!allowed ? <NoAccess /> : (
        <Card aria-label={def.title}>
          <CardHeader title={def.title} subtitle={first?.note ?? undefined} />
          <div className={s.tools}>
            {def.controls === "range" ? <>
              <Field label="From" error={rangeErr && !isDate(from) ? "YYYY-MM-DD" : null}>{(a) => <input {...a} className={s.input} value={from} onChange={(e) => setFrom(e.target.value)} />}</Field>
              <Field label="To" error={rangeErr && isDate(from) ? rangeErr : null}>{(a) => <input {...a} className={s.input} value={to} onChange={(e) => setTo(e.target.value)} />}</Field>
            </> : null}
            {def.controls === "asOf" ? <Field label="Age as of" error={ctlErr}>{(a) => <input {...a} className={s.input} value={asOf} onChange={(e) => setAsOf(e.target.value)} />}</Field> : null}
            {def.controls === "month" ? <Field label="Month" error={ctlErr}>{(a) => <input {...a} className={s.input} value={month} onChange={(e) => setMonth(e.target.value)} placeholder="YYYY-MM" />}</Field> : null}
            {def.groups ? <Field label="Group">{(a) => <select {...a} className={s.input} value={group} onChange={(e) => setGroup(e.target.value)}>{def.groups!.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}</select>}</Field> : null}
          </div>
          {exportErr ? <p className={s.formErr} role="alert">{exportErr}</p> : null}
          {ctlErr ? <Empty icon="alert" title="Check the filters" description={ctlErr} /> : (
            <Gate query={q} what={def.title.toLowerCase()}>
              {() => (
                <>
                  {first && Object.keys(first.totals).length ? <div className={s.strip} aria-label="Totals">{def.totals(first.totals, fmt).map((t) => <div key={t.label}><small>{t.label}</small><b>{t.value}</b></div>)}</div> : null}
                  {rows.length === 0 ? <Empty icon="chart" title={def.emptyTitle} description="Try a wider range or another branch." /> : (
                    <Table label={`${def.title} rows`} columns={cols} rows={rows} rowKey={(r) => JSON.stringify(r)} minWidth={560} summary={`${rows.length} shown`} hasMore={q.hasNextPage} onLoadMore={() => void q.fetchNextPage()} loadingMore={q.isFetchingNextPage} />
                  )}
                </>
              )}
            </Gate>
          )}
        </Card>
      )}
    </div>
  );
}
